import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Bell, MessageCircle, LogOut, LayoutDashboard, ShoppingBag, Store, Inbox, Menu, X, ShieldCheck } from "lucide-react";
import { useAuth, ROLE_THEMES } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import VerifiedBadge from "@/components/VerifiedBadge";
import GlobalSearchBar from "@/components/GlobalSearchBar";
import RoleSwitcher from "@/components/RoleSwitcher";
import { api } from "@/lib/api";
import { DASH } from "@/constants/testIds";

const LOGO = "https://customer-assets.emergentagent.com/job_farmly-connect/artifacts/i2kddnib_ChatGPT%20Image%20Nov%2026%2C%202025%2C%2005_59_04%20PM.png";

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const theme = user ? ROLE_THEMES[user.role] : ROLE_THEMES.farmer;
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const { tick } = useNotifications();

  useEffect(() => {
    if (!user) return;
    api.get("/notifications").then((r) => setUnread(r.data.filter((n) => !n.read).length)).catch(() => {});
  }, [user, loc.pathname, tick]);

  const isActive = (p) => loc.pathname.startsWith(p);
  const { t } = useI18n();
  const navItems = [
    { to: "/dashboard", label: t("nav.dashboard"), icon: LayoutDashboard, tid: "nav-dashboard" },
    { to: "/marketplace", label: t("nav.marketplace"), icon: Store, tid: DASH.goMarketplace },
    { to: "/orders", label: t("nav.orders"), icon: ShoppingBag, tid: DASH.goOrders },
    { to: "/responses", label: t("nav.responses"), icon: Inbox, tid: "nav-responses" },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <header className="sticky top-0 z-40 glass border-b border-white/50">
        <div className="px-4 md:px-8 lg:px-12 py-3 flex items-center justify-between gap-4">
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <img src="https://customer-assets.emergentagent.com/job_farmly-connect/artifacts/i2kddnib_ChatGPT%20Image%20Nov%2026%2C%202025%2C%2005_59_04%20PM.png" alt="GTF" className="h-11 w-11 rounded-2xl bg-white p-0.5 shadow-md object-contain" />
            <div className="hidden sm:block">
              <div className="text-sm font-bold tracking-tight text-emerald-800 leading-tight">Grow Together</div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-600/70 font-semibold">Farmily · GTF</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((n) => (
              <Link key={n.to} to={n.to} data-testid={n.tid}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${isActive(n.to) ? `${theme.bg} text-white shadow-md` : "text-gray-700 hover:bg-gray-100"}`}>
                <n.icon className="h-4 w-4" />{n.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:block flex-1 max-w-md"><GlobalSearchBar /></div>

          <div className="flex items-center gap-2">
            <div className="hidden sm:block"><LanguageSwitcher /></div>
            <RoleSwitcher />
            <Link to="/messages" data-testid={DASH.goMessages} className="h-10 w-10 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50">
              <MessageCircle className="h-4 w-4 text-gray-700" />
            </Link>
            <Link to="/notifications" data-testid={DASH.goNotifications} className="relative h-10 w-10 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50">
              <Bell className="h-4 w-4 text-gray-700" />
              {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] rounded-full px-1 flex items-center justify-center">{unread}</span>}
            </Link>
            <Link to="/profile" data-testid="nav-profile" className={`hidden md:flex items-center gap-2 pl-3 pr-4 py-1.5 rounded-full ${theme.bgSoft} border ${theme.border} hover:opacity-90`}>
              <div className={`h-7 w-7 rounded-full ${theme.bg} text-white flex items-center justify-center text-xs font-bold`}>
                {user?.full_name?.[0]?.toUpperCase() || "U"}
              </div>
              <div className="text-xs">
                <div className={`${theme.text} font-semibold leading-tight flex items-center gap-1`}>{user?.full_name}{user?.is_verified && <ShieldCheck className="h-3 w-3 text-blue-600" />}</div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider">{theme.name}</div>
              </div>
            </Link>
            <button data-testid={DASH.logout} onClick={() => { logout(); nav("/"); }} className="h-10 w-10 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-red-50 hover:text-red-600" title="Logout">
              <LogOut className="h-4 w-4" />
            </button>
            <button className="md:hidden h-10 w-10 rounded-full bg-white border border-gray-200 flex items-center justify-center" onClick={() => setOpen(!open)}>
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden px-4 pb-3 grid grid-cols-2 gap-2">
            {navItems.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                className={`px-3 py-2 rounded-xl text-sm flex items-center gap-2 ${isActive(n.to) ? `${theme.bg} text-white` : "bg-gray-50 text-gray-700"}`}>
                <n.icon className="h-4 w-4" />{n.label}
              </Link>
            ))}
          </div>
        )}
      </header>
      <main data-testid={DASH.container}>{children}</main>
    </div>
  );
}
